import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Activity, 
  UserCheck, 
  FileText, 
  Home, 
  MessageSquare, 
  Bell,
  Clock,
  ArrowRight,
  Loader2,
  RefreshCw
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";

interface ActivityItem {
  id: string;
  type: "role_change" | "claim_status" | "new_claim" | "new_property" | "new_conversation" | "new_message";
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, any>;
}

export function AdminActivityFeed() {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();

    // Set up real-time subscriptions
    const channel = supabase
      .channel('activity-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_role_history' }, () => fetchActivities())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'claim_status_history' }, () => fetchActivities())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'claims' }, () => fetchActivities())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'properties' }, () => fetchActivities())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversations' }, () => fetchActivities())
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchActivities = async () => {
    try {
      const allActivities: ActivityItem[] = [];

      // Fetch role changes
      const { data: roleChanges } = await supabase
        .from("user_role_history")
        .select("id, user_id, old_role, new_role, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      if (roleChanges && roleChanges.length > 0) {
        const userIds = [...new Set(roleChanges.map(r => r.user_id))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", userIds);
        
        const emailMap = new Map((profiles || []).map(p => [p.id, p.email]));

        roleChanges.forEach(change => {
          allActivities.push({
            id: `role-${change.id}`,
            type: "role_change",
            title: "Role Changed",
            description: `${emailMap.get(change.user_id) || "User"}: ${change.old_role || "none"} → ${change.new_role}`,
            timestamp: change.created_at,
            metadata: { old_role: change.old_role, new_role: change.new_role }
          });
        });
      }

      // Fetch claim status changes
      const { data: statusChanges } = await supabase
        .from("claim_status_history")
        .select("id, claim_id, old_status, new_status, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      if (statusChanges && statusChanges.length > 0) {
        const claimIds = [...new Set(statusChanges.map(s => s.claim_id))];
        const { data: claims } = await supabase
          .from("claims")
          .select("id, claim_number")
          .in("id", claimIds);
        
        const claimMap = new Map((claims || []).map(c => [c.id, c.claim_number]));

        statusChanges.forEach(change => {
          allActivities.push({
            id: `status-${change.id}`,
            type: "claim_status",
            title: "Claim Status Updated",
            description: `Claim #${claimMap.get(change.claim_id) || "Unknown"}: ${change.old_status || "new"} → ${change.new_status}`,
            timestamp: change.created_at,
            metadata: { old_status: change.old_status, new_status: change.new_status }
          });
        });
      }

      // Fetch new claims
      const { data: newClaims } = await supabase
        .from("claims")
        .select("id, claim_number, policyholder_name, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      newClaims?.forEach(claim => {
        allActivities.push({
          id: `claim-${claim.id}`,
          type: "new_claim",
          title: "New Claim Submitted",
          description: `Claim #${claim.claim_number} by ${claim.policyholder_name}`,
          timestamp: claim.created_at
        });
      });

      // Fetch new properties
      const { data: newProperties } = await supabase
        .from("properties")
        .select("id, name, city, state, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      newProperties?.forEach(property => {
        allActivities.push({
          id: `property-${property.id}`,
          type: "new_property",
          title: "New Property Added",
          description: `${property.name} in ${property.city}, ${property.state}`,
          timestamp: property.created_at
        });
      });

      // Fetch new support conversations
      const { data: newConversations } = await supabase
        .from("conversations")
        .select("id, subject, priority, created_at, created_by")
        .in("type", ["support", "claim_inquiry"])
        .order("created_at", { ascending: false })
        .limit(10);

      if (newConversations && newConversations.length > 0) {
        const creatorIds = [...new Set(newConversations.map(c => c.created_by))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, email")
          .in("id", creatorIds);
        
        const emailMap = new Map((profiles || []).map(p => [p.id, p.email]));

        newConversations.forEach(conv => {
          allActivities.push({
            id: `conv-${conv.id}`,
            type: "new_conversation",
            title: "New Support Request",
            description: `"${conv.subject}" from ${emailMap.get(conv.created_by) || "User"}`,
            timestamp: conv.created_at,
            metadata: { priority: conv.priority }
          });
        });
      }

      // Sort all activities by timestamp
      allActivities.sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      // Take the most recent 20
      setActivities(allActivities.slice(0, 20));
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: ActivityItem["type"]) => {
    switch (type) {
      case "role_change":
        return <UserCheck className="h-4 w-4" />;
      case "claim_status":
      case "new_claim":
        return <FileText className="h-4 w-4" />;
      case "new_property":
        return <Home className="h-4 w-4" />;
      case "new_conversation":
      case "new_message":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getActivityColor = (type: ActivityItem["type"]) => {
    switch (type) {
      case "role_change":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "claim_status":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "new_claim":
        return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "new_property":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300";
      case "new_conversation":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const getTypeBadge = (type: ActivityItem["type"]) => {
    const labels: Record<ActivityItem["type"], string> = {
      role_change: "Role",
      claim_status: "Status",
      new_claim: "Claim",
      new_property: "Property",
      new_conversation: "Support",
      new_message: "Message"
    };
    return labels[type] || "Activity";
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Activity Feed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Activity Feed
            </CardTitle>
            <CardDescription>Recent changes across all entities</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchActivities}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[400px]">
          <div className="px-4 pb-4">
            {activities.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mb-2 opacity-50" />
                <p>No recent activity</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />
                
                <div className="space-y-4">
                  {activities.map((activity, index) => (
                    <div key={activity.id} className="relative flex gap-3 pl-1">
                      {/* Timeline dot */}
                      <div className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${getActivityColor(activity.type)}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {getTypeBadge(activity.type)}
                          </Badge>
                          {activity.metadata?.priority === "urgent" && (
                            <Badge variant="destructive" className="text-xs">
                              Urgent
                            </Badge>
                          )}
                          {activity.metadata?.priority === "high" && (
                            <Badge className="bg-orange-500 text-xs">
                              High
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm font-medium mt-1">{activity.title}</p>
                        <p className="text-sm text-muted-foreground truncate">{activity.description}</p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          <span title={format(new Date(activity.timestamp), "PPpp")}>
                            {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
